import { Injectable, inject } from '@angular/core';
import {
  AssetService,
  MachineGroupInfo,
  MachineGroupService,
  MachineInfo,
  MachineService,
  PlantInfo,
  PlantService,
  SpinnerV2Service,
  StoreV2,
  ToolingService,
} from '@schenck/rotec-components';
import { BehaviorSubject, combineLatest, merge, Observable } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { KpiService, MachineInfoWithKpi, ToolingInfoWithKpi } from './kpi.service';
import { objContainsFilterStr } from 'src/utils';

export interface TileViewState {
  order: string[];
  filter: string;
  selectedPlant: string;
  selectedGroup: string;
}

@Injectable()
export class TileViewService extends StoreV2<TileViewState> {
  private readonly assetService = inject(AssetService);
  private readonly kpiService = inject(KpiService);
  private readonly loadingService = inject(SpinnerV2Service);
  private readonly machineService = inject(MachineService);
  private readonly toolingService = inject(ToolingService);
  private readonly plantService = inject(PlantService);
  private readonly machineGroupService = inject(MachineGroupService);

  public get tileViewServiceError$(): Observable<boolean | string> {
    return merge(
      this.machineService.stateError$,
      this.machineGroupService.stateError$,
      this.plantService.stateError$,
      this.toolingService.stateError$,
      this.kpiService.kpiServiceError$
    ).pipe(filter((value) => value === true || typeof value === 'string'));
  }

  selectedPlant$ = this.state$.pipe(map((state) => state.selectedPlant));
  selectedGroup$ = this.state$.pipe(map((state) => state.selectedGroup));

  visibleMachines$: Observable<MachineInfoWithKpi[]> = combineLatest([
    this.selectedPlant$,
    this.selectedGroup$,
  ]).pipe(
    switchMap(([selectedPlant, selectedGroup]) => {
      if (!selectedGroup && !selectedPlant) {
        return this.kpiService.machinesWithTasks$;
      }

      const filteredMachines$ = this.getFilteredMachines$(selectedPlant, selectedGroup);

      return combineLatest([filteredMachines$, this.kpiService.machinesWithTasks$]).pipe(
        map(([filteredMachines, machinesWithTasks]) => {
          return machinesWithTasks.filter((machine) =>
            filteredMachines.find((_machine) => _machine.id === machine.id)
          );
        })
      );
    })
  );

  visibleToolings$: Observable<ToolingInfoWithKpi[]> = combineLatest([
    this.selectedPlant$,
    this.selectedGroup$,
  ]).pipe(
    switchMap(([selectedPlant, selectedGroup]) => {
      if (!selectedGroup && !selectedPlant) {
        return this.kpiService.toolingsWithTasks$;
      }

      const filteredMachines$ = this.getFilteredMachines$(selectedPlant, selectedGroup);

      return combineLatest([
        this.assetService.toolingsOfMachines$(filteredMachines$),
        this.kpiService.toolingsWithTasks$,
      ]).pipe(
        map(([filteredToolings, toolingsWithTasks]) => {
          return toolingsWithTasks.filter((tooling) =>
            filteredToolings.find((_tooling) => _tooling.id === tooling.id)
          );
        })
      );
    })
  );

  tiles$: Observable<
    (
      | ({
          _assetType: string;
          plantName: string;
          machineGroups: string;
        } & MachineInfoWithKpi)
      | ({
          _assetType: string;
          machines: string;
        } & ToolingInfoWithKpi)
    )[]
  >;

  constructor() {
    super(
      {
        order: [],
        filter: '',
        selectedPlant: '',
        selectedGroup: '',
      },
      'TileViewService'
    );

    this.loadingService.setLoading('overview', true);

    this.tiles$ = combineLatest([
      this.visibleMachines$,
      this.visibleToolings$,
      this.assetService.plants$,
      this.assetService.machineGroups$,
      this.state$,
    ]).pipe(
      map(([machines, toolings, plants, machineGroups, state]) => {
        const tiles = [
          ...machines
            .filter((machine) => !machine.isDeactivated)
            .map((m) => mapMachine(m, plants, machineGroups)),
          ...toolings
            .filter((tooling) => !tooling.isDeactivated)
            .map((t) => mapTooling(t, machines)),
        ].sort((a, b) => {
          if (state.order.indexOf(a.id) < state.order.indexOf(b.id)) {
            return -1;
          }
          if (state.order.indexOf(a.id) > state.order.indexOf(b.id)) {
            return 1;
          }
          return 0;
        });
        this.loadingService.setLoading('overview', false);
        if (!!state.filter) {
          return tiles.filter((tile) => objContainsFilterStr(tile, state.filter));
        } else {
          return tiles;
        }
      })
    );
  }

  updateOrder(order: string[]) {
    this.patchState('updated tile order', { order: order });
  }

  filter(filter: string) {
    this.patchState('filter tiles', { filter: filter });
  }

  filterForPlant(selectedPlant: string) {
    const selectedGroup =
      this.assetService
        .machineGroupsByPlantId(selectedPlant)
        .find(({ id }) => this.state.selectedGroup === id)?.id || '';

    this.patchState('filter for plant', { selectedPlant, selectedGroup });
  }

  filterForGroup(selectedGroup: string) {
    const selectedPlant =
      this.assetService
        .plantsByMachineGroupId(selectedGroup)
        .find(({ id }) => this.state.selectedPlant === id)?.id || '';

    this.patchState('filter for group', { selectedGroup, selectedPlant });
  }

  private getFilteredMachines$(
    selectedPlant: string,
    selectedGroup: string
  ): Observable<MachineInfo[]> {
    if (selectedPlant && !selectedGroup) {
      return this.assetService.machinesInsidePlant$(selectedPlant);
    } else if (selectedGroup && !selectedPlant) {
      return this.assetService.machinesInsideGroup$(selectedGroup);
    } else {
      return this.assetService
        .machinesInsideGroup$(selectedGroup)
        .pipe(map((machines) => machines.filter(({ plantId }) => plantId === selectedPlant)));
    }
  }

  private getTiles(
    machinesWithTasks: MachineInfo[],
    plants: PlantInfo[],
    machineGroups: MachineGroupInfo[],
    toolingsWithTasks: any[],
    machines: MachineInfo[]
  ) {
    return [
      ...machinesWithTasks.map((m) => ({
        ...m,
        _assetType: 'machine',
        plantName: plants.find((plant) => plant.id === m.plantId).name,
        machineGroups: machineGroups
          .filter((machineGroup) => machineGroup.childMachineIds.includes(m.id))
          .map((machineGroup) => machineGroup.name)
          .join(','),
      })),
      ...toolingsWithTasks
        .map((t) => ({
          ...t,
          _assetType: 'tooling',
          machines: machines
            .filter((machine) => t.machineIds.includes(machine.id))
            .map((machine) => machine.name)
            .join(','),
        }))
        .filter((tooling) => !tooling.isDeactivated),
    ];
  }

  private sortTiles(tiles: any[], order: string[]) {
    if (order.length === 0) {
      return this.sortAccordingToOverdue(tiles);
    }

    return this.sortAccordingToOrder(tiles, order);
  }

  private sortAccordingToOverdue(tiles: any[]) {
    return tiles.sort((a, b) => {
      if (a.kpi.numberTasksOverdue > b.kpi.numberTasksOverdue) {
        return -1;
      }
      if (a.kpi.numberTasksOverdue < b.kpi.numberTasksOverdue) {
        return 1;
      }
      return 0;
    });
  }

  private sortAccordingToOrder(tiles: any[], order: string[]) {
    return tiles.sort((a, b) => {
      if (order.indexOf(a.id) < order.indexOf(b.id)) {
        return -1;
      }
      if (order.indexOf(a.id) > order.indexOf(b.id)) {
        return 1;
      }
      return 0;
    });
  }

  private filterTiles(tiles: any[], state: TileViewState) {
    if (!!state.filter) {
      return tiles.filter((tile) =>
        Object.keys(tile).some((key) => {
          if (tile[key] && typeof tile[key] === 'string') {
            return tile[key].toLowerCase().includes(state.filter.toLowerCase());
          }
        })
      );
    } else {
      return tiles;
    }
  }
}

function mapTooling(
  t: ToolingInfoWithKpi,
  machines: MachineInfo[]
): {
  _assetType: string;
  machines: string;
} & ToolingInfoWithKpi {
  return {
    ...t,
    _assetType: 'tooling',
    machines: machines
      .filter((machine) => t.machineIds.includes(machine.id) && !machine.isDeactivated)
      .map((machine) => machine.name)
      .join(','),
  };
}

function mapMachine(
  m: MachineInfoWithKpi,
  plants: PlantInfo[],
  machineGroups: MachineGroupInfo[]
): {
  _assetType: string;
  plantName: string;
  machineGroups: string;
} & MachineInfoWithKpi {
  return {
    ...m,
    _assetType: 'machine',
    plantName: plants.find((plant) => plant.id === m.plantId).name,
    machineGroups: machineGroups
      .filter((machineGroup) => machineGroup.childMachineIds.includes(m.id))
      .map((machineGroup) => machineGroup.name)
      .join(','),
  };
}
